import { useCallback, useEffect, useState } from "react";

import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Download } from "lucide-react";
import { useDispatch } from "react-redux";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { Button } from "@components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { setMessage } from "@redux/notification";

import { callApi } from "@utils/callApi";

import "./index.css";

interface ReportFile {
  id?: string;
  name?: string;
  download_url?: string;
  status?: string;
  created_at?: string;
}

const ReportAssistantHistory = (props) => {
  const { id: caseNumber } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();

  const [reportGeneratorFiles, setReportGeneratorFiles] = useState<ReportFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const dispatch = useDispatch();

  const handleBackClick = () => {
    navigate({ to: `/report-studio/${caseNumber}/edge-compose` });
  };

  const fetchReports = useCallback(
    async (queryParams = {}) => {
      try {
        setIsLoading(true);

        const response = await callApi(
          `report-assistant/cases/${caseNumber}/generated-reports`,
          "GET"
        );

        setReportGeneratorFiles(response);
      } catch (_error) {
        dispatch(
          setMessage({
            content: "Failed to fetch cases",
            type: "error",
          })
        );
      } finally {
        setIsLoading(false);
      }
    },
    [caseNumber, dispatch]
  );

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  return (
    <div className="case-list-container">
      <div className="mb-4 mt-4 flex items-center">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="back"
              onClick={handleBackClick}
              className="ml-2 mr-2"
            >
              <ArrowLeft size={20} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Back to Report Builder</TooltipContent>
        </Tooltip>
        <div className="flex-1 text-left text-xl font-bold">
          Generated Report History
        </div>
      </div>
      <div className="main-content">
        <div className="table-container">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingIcon className="h-20 w-20" />
            </div>
          ) : reportGeneratorFiles.length > 0 ? (
            <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-background-white">
                  <TableRow>
                    <TableHead className="font-montserrat font-bold">
                      Template Name
                    </TableHead>
                    <TableHead className="font-montserrat font-bold">
                      Generated Report URL
                    </TableHead>
                    <TableHead className="font-montserrat font-bold">
                      Status
                    </TableHead>
                    <TableHead className="font-montserrat font-bold">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportGeneratorFiles.map((row, index) => (
                    <TableRow
                      key={row.id || index}
                      className="cursor-pointer font-montserrat"
                    >
                      <TableCell className="font-medium">{row.name || ""}</TableCell>
                      <TableCell>
                        {row.download_url ? (
                          <a
                            href={row.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              textDecoration: "none",
                              color: "inherit",
                            }}
                            className="hover:underline"
                          >
                            <Download size={16} style={{ marginRight: 8 }} />
                            Download
                          </a>
                        ) : (
                          "No URL"
                        )}
                      </TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>
                        {(() => {
                          if (!row.created_at) return "";
                          const date = new Date(row.created_at);
                          const hours = date.getHours();
                          const minutes = date.getMinutes();
                          const ampm = hours >= 12 ? "PM" : "AM";
                          const formattedHours = hours % 12 || 12;
                          const formattedMinutes =
                            minutes < 10 ? `0${minutes}` : minutes;
                          return (
                            date.toISOString().split("T")[0] +
                            " " +
                            formattedHours +
                            ":" +
                            formattedMinutes +
                            " " +
                            ampm
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="no-case-indicator">
              <p>No Reports Found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportAssistantHistory;
